import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import '@/styles/article.css';
import '@/styles/landing.css';

// A fragment, not a wrapper. article.css binds the measure and the paragraph
// rhythm with CHILD selectors (`.uxa-article > p`), so the h2 and the prose have
// to be direct children of the article. Wrapping them in two divs is what kept
// these pages at 14px while the blog moved to 19.
const Section = ({ title, children }) => (
  <>
    <h2>{title}</h2>
    {children}
  </>
);

export default function TermsPage() {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Terms of Service"
        description="Read the UXAuditPro Terms of Service — the rules and conditions that govern your use of our AI-powered UX audit platform."
        canonical="/terms"
      />
      {/* THE MEASURE STAYS AT max-w-3xl. These are article pages; article.css
          binds --article-measure to 68ch and the module was verified conformant
          on 2026-08-19. The brand shell goes around the column, never through
          it. The grid wash sits behind the document header only. */}
      <div className="max-w-3xl mx-auto px-6 py-16" style={{ position: 'relative' }}>
        {/* Header */}
        <div className="mb-12" style={{ position: 'relative' }}>
          <div className="uxa-hero-grid" aria-hidden="true" /* inset was '-64px -40px 0' and produced 16px of horizontal
                   overflow at 390 and 768 — the column is px-6 (24px), so a
                   -40px bleed has nowhere to go. Measured on the built page:
                   scrollWidth 406 against a 390 client. A decorative wash is
                   never a reason for the page to scroll sideways. */
                style={{ inset: '-64px 0 0' }} />
          <div style={{ position: 'relative' }}>
          <span className="text-sm font-bold uppercase tracking-widest" style={{ color: 'var(--accent-text)' }}>Legal</span>
          <h1 className="uxa-h1 mt-3" style={{ color: 'var(--ink-1)', textWrap: 'pretty' }}>
            Terms of Service
          </h1>
          <p className="uxa-meta mt-3">Last updated: 7 October 2026</p>
          </div>
        </div>

        <div className="uxa-article uxa-feature-card" style={{ background: 'var(--surface-white)', padding: 'var(--space-10)' }}>
          <Section title="1. Acceptance of Terms">
            <p>By accessing or using UXAuditPro ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.</p>
          </Section>

          <Section title="2. Description of Service">
            <p>UXAuditPro provides AI-powered UX audit reports for websites and applications. The Service analyses submitted screenshots, URLs, and contextual information to generate structured UX feedback and recommendations.</p>
            <p>Features and availability vary by plan. We reserve the right to modify or discontinue features at any time with reasonable notice.</p>
          </Section>

          <Section title="3. Account Registration">
            <p>You must create an account to use the Service. You are responsible for maintaining the security of your account credentials and for all activity that occurs under your account.</p>
            <p>You must provide accurate and complete information when registering. Accounts may not be shared or transferred to another person without our written consent.</p>
          </Section>

          <Section title="4. Plans and Billing">
            <p>The Service is offered on three plans. Free gives each account one audit every calendar month at no charge. Starter is a one-time payment per audit, with no subscription and no recurring charge. Agency is arranged with us directly: its price, billing period and what it includes are agreed with you before any charge is made.</p>
            <p>Starter audits and add-ons are paid through PayPal or Razorpay. Refunds are handled case by case: contact <a href="mailto:support@uxauditpro.com">support@uxauditpro.com</a> within 7 days of a charge if you believe you were billed in error.</p>
            <p>Free and Starter have nothing to cancel, because nothing recurs. An Agency arrangement ends on the terms agreed with us.</p>
          </Section>

          <Section title="5. Acceptable Use">
            <p>You agree not to use the Service to upload content that is illegal, harmful, or infringes third-party intellectual property rights. You may not attempt to reverse-engineer, scrape, or disrupt the Service.</p>
            <p>Audit credits are for your own use only and may not be resold or transferred, except as permitted by white-label plan terms.</p>
          </Section>

          <Section title="6. Intellectual Property">
            <p>You retain ownership of all content you submit (screenshots, URLs, descriptions). By submitting content, you grant UXAuditPro a limited license to process and analyse that content solely to provide the Service.</p>
            <p>The generated audit reports are yours to use and distribute. The underlying AI models, platform, and product are the intellectual property of UXAuditPro.</p>
          </Section>

          <Section title="7. Disclaimer of Warranties">
            <p>The Service is provided "as is" without warranties of any kind. We do not guarantee that audit results will be error-free, complete, or suitable for any particular purpose. UX recommendations are AI-generated and should be reviewed by a qualified professional before implementation.</p>
          </Section>

          <Section title="8. Limitation of Liability">
            <p>To the maximum extent permitted by law, UXAuditPro shall not be liable for any indirect, incidental, or consequential damages arising from your use of the Service. Our total liability to you in any calendar month shall not exceed the amount you paid us in that month.</p>
          </Section>

          <Section title="9. Changes to Terms">
            <p>We may update these Terms at any time. We will notify you of material changes via email or in-app notice. Continued use of the Service after changes constitutes your acceptance.</p>
          </Section>

          <Section title="10. Governing Law">
            <p>These Terms are governed by the laws of the jurisdiction in which UXAuditPro operates, without regard to conflict of law principles.</p>
          </Section>

          <Section title="11. Contact">
            <p>For questions about these Terms, contact us at <a href="mailto:legal@uxauditpro.com">legal@uxauditpro.com</a>.</p>
          </Section>

          <Section title="12. Eligibility">
            <p>You must be at least 18 years old to use the Service.</p>
          </Section>
        </div>

        {/* 44px MINIMUM. These measured 88x20, 53x20 and 34x20 on the built
            page at all four widths. They are a standalone document-switcher
            row, not links inside a sentence, so WCAG 2.5.8's inline-prose
            exemption does not reach them. Presented as a 44px hit area via
            inline-flex so the row does not move. */}
        <div className="mt-8 flex gap-4 text-sm items-center" style={{ color: 'var(--ink-2)' }}>
          <Link to="/privacy/" style={{ color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', minHeight: 44 }}>Privacy Policy</Link>
          <span>·</span>
          <Link to="/security/" style={{ color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', minHeight: 44 }}>Security</Link>
          <span>·</span>
          <Link to="/legal/" style={{ color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: 44, minWidth: 44 }}>Legal</Link>
        </div>
      </div>
    </div>
  );
}
